// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useRef } from 'react'

type SetInput = (value: string) => void

export function useTextareaControls(
    input: string,
    setInput: SetInput,
    onEnter: () => void
) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const isComposingRef = useRef(false)

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                const native: any = e.nativeEvent as any
                if (native?.isComposing || isComposingRef.current) return
                e.preventDefault()
                onEnter()
            }
        },
        [onEnter]
    )

    const handleCompositionStart = useCallback(() => {
        isComposingRef.current = true
    }, [])

    const handleCompositionEnd = useCallback(() => {
        isComposingRef.current = false
        // Ensure state reflects the full composed value after IME finishes
        if (textareaRef.current) {
            const val = textareaRef.current.value ?? ''
            setInput(val)
        }
    }, [setInput])

    useEffect(() => {
        const el = textareaRef.current
        if (!el) return
        const maxPx = 128
        el.style.height = 'auto'
        const next = Math.min(el.scrollHeight, maxPx)
        el.style.height = `${next}px`
    }, [input])

    return {
        textareaRef,
        isComposingRef,
        handleKeyDown,
        handleCompositionStart,
        handleCompositionEnd,
    }
}


